import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { signInFailureHint } from './signInFailure';

/**
 * Wraps an app in MSAL. MSAL v5 requires initialize() to resolve before use, so children
 * mount only once the instance is ready. handleRedirectPromise() must also settle before
 * anything reads accounts: on the way back from the sign-in redirect it is what turns the
 * URL fragment into a cached account, and skipping it leaves the app looking signed out.
 */
export function AuthProvider({ config, children }: { config: Configuration; children: ReactNode }) {
  const instance = useMemo(() => new PublicClientApplication(config), [config]);
  const [ready, setReady] = useState(false);
  // The error itself, not only its message: which line goes under it depends on what kind it is.
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;

    void instance
      .initialize()
      .then(() => instance.handleRedirectPromise())
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((cause: unknown) => {
        // Surface the failure instead of mounting an app that cannot acquire tokens.
        if (!cancelled) setError({ cause });
      });

    return () => {
      cancelled = true;
    };
  }, [instance]);

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="max-w-sm text-center">
          <p className="font-semibold text-ink-950">Sign-in is unavailable</p>
          <p className="mt-1 text-sm text-ink-800/70">
            {error.cause instanceof Error ? error.cause.message : 'Unknown error'}
          </p>
          {/* Only a configuration-shaped error is told to check the configuration — see signInFailure.ts. */}
          <p className="mt-3 text-xs text-ink-800/50">{signInFailureHint(error.cause)}</p>
        </div>
      </div>
    );
  }

  if (!ready) return null;
  return <MsalProvider instance={instance}>{children}</MsalProvider>;
}
